/**
 * Cargar una lista de correos. Dos vías, y la primera es la de dentro.
 *
 * Desde contactos: se marcan uno o varios ORÍGENES del CRM y entran los que
 * tengan correo. Es la vía natural cuando la gente ya está en EvoHub — pedir
 * que la exporten a CSV y la vuelvan a subir sería hacerles rodear la base de
 * datos para llegar a la base de datos.
 *
 * Desde un archivo: para una lista que llega de fuera. El emparejado de
 * columnas no se le pide a nadie — se detecta y se MUESTRA con un ejemplo al
 * lado, porque quien importa no quiere configurar un mapeo, quiere ver que
 * acertamos.
 *
 * Lo único que las dos exigen es de dónde salieron las direcciones, sin valor
 * por defecto: es la única respuesta a un reclamo de spam meses después.
 */
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../ui/Modal.js';
import { Button } from '../../ui/Button.js';
import { Input } from '../../ui/Input.js';
import { Badge } from '../../ui/Badge.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { LoadingState } from '../../ui/LoadingState.js';
import {
  useImportarLista, useImportarDesdeContactos, useOrigenesDeContactos,
  type ResumenImportacion,
} from '../../../hooks/useNewsletter.js';

interface Props {
  projectId: number;
  /** Si el proyecto todavía no tiene lista, hace falta el remitente para crearla. */
  necesitaRemitente: boolean;
  onClose: () => void;
}

type Via = 'contactos' | 'archivo';
type Paso = 'elegir' | 'revisar' | 'listo';

interface FilaLeida { email: string; nombre: string | null }

function partirCsv(texto: string): { cabeceras: string[]; filas: Record<string, string>[] } {
  const lineas = texto.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim() !== '');
  if (lineas.length === 0) return { cabeceras: [], filas: [] };
  const limpia = (v: string): string => v.trim().replace(/^"|"$/g, '');
  const cabeceras = (lineas[0] ?? '').split(',').map(limpia);
  const filas = lineas.slice(1).map(l => {
    const celdas = l.split(',').map(limpia);
    return Object.fromEntries(cabeceras.map((c, i) => [c, celdas[i] ?? '']));
  });
  return { cabeceras, filas };
}

/** Qué columna es el correo y cuál el nombre. Si no acierta, se dice. */
function detectar(cabeceras: string[]): { correo: string | null; nombre: string | null } {
  const correo = cabeceras.find(c => /e-?mail|correo/i.test(c)) ?? null;
  const nombre = cabeceras.find(c => /nombre|name|contacto/i.test(c) && c !== correo) ?? null;
  return { correo, nombre };
}

export function ImportarListaModal({ projectId, necesitaRemitente, onClose }: Props) {
  const { t } = useTranslation();
  const desdeArchivo = useImportarLista(projectId);
  const desdeContactos = useImportarDesdeContactos(projectId);
  const { data: origenesData, isLoading: cargandoOrigenes } = useOrigenesDeContactos(projectId);

  const [via, setVia] = useState<Via>('contactos');
  const [paso, setPaso] = useState<Paso>('elegir');

  // Vía contactos
  const [marcados, setMarcados] = useState<string[]>([]);

  // Vía archivo
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [cabeceras, setCabeceras] = useState<string[]>([]);
  const [crudas, setCrudas] = useState<Record<string, string>[]>([]);
  const [colCorreo, setColCorreo] = useState<string | null>(null);
  const [colNombre, setColNombre] = useState<string | null>(null);
  const archivoRef = useRef<HTMLInputElement>(null);

  const [procedencia, setProcedencia] = useState('');
  const [remitente, setRemitente] = useState('');
  const [error, setError] = useState('');
  const [resumen, setResumen] = useState<ResumenImportacion | null>(null);

  const origenes = origenesData?.origenes ?? [];
  const sumaMarcada = origenes
    .filter(o => marcados.includes(o.origen))
    .reduce((n, o) => n + o.conCorreo, 0);

  function leerArchivo(file: File): void {
    const lector = new FileReader();
    lector.onload = (e) => {
      const { cabeceras: cab, filas } = partirCsv(String(e.target?.result ?? ''));
      if (cab.length === 0 || filas.length === 0) {
        setError(t('newsletter.importar.vacio', { defaultValue: 'El archivo no tiene filas.' }));
        return;
      }
      const d = detectar(cab);
      setNombreArchivo(file.name);
      setCabeceras(cab);
      setCrudas(filas);
      setColCorreo(d.correo);
      setColNombre(d.nombre);
      setError('');
      setPaso('revisar');
    };
    lector.onerror = () => setError(t('newsletter.importar.ilegible', { defaultValue: 'No se pudo leer el archivo.' }));
    lector.readAsText(file, 'utf-8');
  }

  const filasListas: FilaLeida[] = colCorreo
    ? crudas.map(f => ({
      email: (f[colCorreo] ?? '').trim(),
      nombre: colNombre ? ((f[colNombre] ?? '').trim() || null) : null,
    })).filter(f => f.email !== '')
    : [];

  const cuantosEntran = via === 'contactos' ? sumaMarcada : filasListas.length;
  const trabajando = desdeArchivo.isPending || desdeContactos.isPending;
  const puedeImportar =
    cuantosEntran > 0
    && procedencia.trim().length >= 10
    && (!necesitaRemitente || remitente.trim().length > 0);

  async function confirmar(): Promise<void> {
    setError('');
    const comunes = {
      procedencia: procedencia.trim(),
      ...(necesitaRemitente ? { remitente: remitente.trim() } : {}),
    };
    try {
      const r = via === 'contactos'
        ? await desdeContactos.mutateAsync({ origenes: marcados, ...comunes })
        : await desdeArchivo.mutateAsync({ filas: filasListas, ...comunes });
      setResumen(r.resumen);
      setPaso('listo');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const tenue = { color: 'var(--evo-fg-3)', fontSize: 'var(--evo-fs-xs)' };
  const columna = { display: 'flex', flexDirection: 'column' as const, gap: 'var(--evo-space-4)' };

  const camposComunes = (
    <>
      {necesitaRemitente && (
        <Input
          label={t('newsletter.importar.remitente', { defaultValue: 'Remitente' })}
          value={remitente}
          onChange={setRemitente}
          placeholder="Nombre <correo@dominio-verificado.com>"
        />
      )}
      <Input
        label={t('newsletter.importar.procedencia', { defaultValue: '¿De dónde salieron estas direcciones?' })}
        value={procedencia}
        onChange={setProcedencia}
        placeholder={t('newsletter.importar.procedenciaEjemplo', {
          defaultValue: 'Clientes que contrataron entre 2024 y 2026',
        })}
      />
      <p style={{ margin: 0, ...tenue }}>
        {t('newsletter.importar.porQue', {
          defaultValue: 'Se guarda con cada contacto. Es lo que responde un reclamo meses después.',
        })}
      </p>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t('newsletter.importar.title', { defaultValue: 'Cargar lista de correos' })}
    >
      <div style={columna}>

        {paso !== 'listo' && (
          <SegmentedControl
            value={via}
            onChange={(v) => { setVia(v as Via); setPaso('elegir'); setError(''); }}
            options={[
              { value: 'contactos', label: t('newsletter.importar.viaContactos', { defaultValue: 'Desde mis contactos' }) },
              { value: 'archivo', label: t('newsletter.importar.viaArchivo', { defaultValue: 'Desde un archivo' }) },
            ]}
          />
        )}

        {/* ── Desde los contactos del CRM ── */}
        {paso !== 'listo' && via === 'contactos' && (
          <>
            <p style={{ margin: 0, ...tenue }}>
              {t('newsletter.importar.ayudaOrigenes', {
                defaultValue: 'Marca uno o varios orígenes. Entran los contactos que tengan correo.',
              })}
            </p>

            {cargandoOrigenes
              ? <LoadingState size="sm" />
              : origenes.length === 0
                ? <p style={{ margin: 0, ...tenue }}>{t('newsletter.importar.sinOrigenes', { defaultValue: 'No hay contactos con correo en el CRM.' })}</p>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', maxHeight: 220, overflowY: 'auto' }}>
                    {origenes.map(o => (
                      <label
                        key={o.origen}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)',
                          cursor: 'pointer', fontSize: 'var(--evo-fs-sm)',
                        }}
                      >
                        <Checkbox
                          checked={marcados.includes(o.origen)}
                          onChange={(v) => setMarcados(prev => (v ? [...prev, o.origen] : prev.filter(x => x !== o.origen)))}
                        />
                        <span style={{ color: 'var(--evo-fg-1)' }}>{o.origen}</span>
                        <Badge tone="muted">{o.conCorreo}</Badge>
                      </label>
                    ))}
                  </div>
                )}

            {marcados.length > 0 && (
              <Badge tone="cyan">
                {t('newsletter.importar.sumaMarcada', { defaultValue: '{{n}} contactos seleccionados', n: sumaMarcada })}
              </Badge>
            )}

            {camposComunes}
          </>
        )}

        {/* ── Desde un archivo ── */}
        {paso === 'elegir' && via === 'archivo' && (
          <>
            <p style={{ margin: 0, ...tenue }}>
              {t('newsletter.importar.ayuda', {
                defaultValue: 'Un archivo CSV con una columna de correo. Si viene de Excel, guárdalo como CSV.',
              })}
            </p>
            <Button variant="outline" onClick={() => archivoRef.current?.click()}>
              {t('newsletter.importar.elegir', { defaultValue: 'Elegir archivo CSV' })}
            </Button>
            <input
              ref={archivoRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) leerArchivo(f); }}
            />
          </>
        )}

        {paso === 'revisar' && via === 'archivo' && (
          <>
            <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'center' }}>
              <Badge tone="cyan">{nombreArchivo}</Badge>
              <span style={tenue}>
                {t('newsletter.importar.detectadas', { defaultValue: '{{n}} direcciones', n: filasListas.length })}
              </span>
            </div>

            {colCorreo
              ? (
                <p style={{ margin: 0, fontSize: 'var(--evo-fs-sm)', color: 'var(--evo-fg-2)' }}>
                  {t('newsletter.importar.columnas', {
                    defaultValue: 'Correo desde «{{correo}}»{{nombre}}. Ejemplo: {{ejemplo}}',
                    correo: colCorreo,
                    nombre: colNombre ? t('newsletter.importar.yNombre', { defaultValue: ', nombre desde «{{c}}»', c: colNombre }) : '',
                    ejemplo: filasListas[0]?.email ?? '—',
                  })}
                </p>
              )
              : (
                <p style={{ margin: 0, fontSize: 'var(--evo-fs-sm)', color: 'var(--evo-danger)' }}>
                  {t('newsletter.importar.sinColumna', {
                    defaultValue: 'No encontré una columna de correo. Las del archivo son: {{cols}}',
                    cols: cabeceras.join(', '),
                  })}
                </p>
              )}

            {camposComunes}
          </>
        )}

        {error && paso !== 'listo' && (
          <p style={{ margin: 0, color: 'var(--evo-danger)', fontSize: 'var(--evo-fs-sm)' }}>{error}</p>
        )}

        {paso !== 'listo' && (via === 'contactos' || paso === 'revisar') && (
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)', justifyContent: 'flex-end' }}>
            {via === 'archivo' && (
              <Button variant="ghost" tone="muted" onClick={() => setPaso('elegir')}>
                {t('common.back', { defaultValue: 'Atrás' })}
              </Button>
            )}
            <Button onClick={() => { void confirmar(); }} disabled={!puedeImportar} loading={trabajando}>
              {t('newsletter.importar.confirmar', { defaultValue: 'Importar {{n}}', n: cuantosEntran })}
            </Button>
          </div>
        )}

        {/* ── Resultado ── */}
        {paso === 'listo' && resumen && (
          <>
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-sm)' }}>
              {t('newsletter.importar.resultado', {
                defaultValue: '{{agregados}} agregadas · {{repetidos}} ya estaban · {{deBaja}} dadas de baja (no se resucitan)',
                agregados: resumen.agregados, repetidos: resumen.repetidos, deBaja: resumen.deBaja,
              })}
            </p>
            {resumen.invalidos.length > 0 && (
              <div>
                <p style={{ margin: '0 0 var(--evo-space-2)', ...tenue }}>
                  {t('newsletter.importar.invalidas', {
                    defaultValue: '{{n}} no parecían correos y quedaron fuera:', n: resumen.invalidos.length,
                  })}
                </p>
                <ul style={{ margin: 0, paddingLeft: 'var(--evo-space-4)', ...tenue }}>
                  {resumen.invalidos.slice(0, 8).map(i => (
                    <li key={i.fila}>{t('newsletter.importar.fila', { defaultValue: 'fila {{f}}', f: i.fila })}: {i.valor || '—'}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={onClose}>{t('common.done', { defaultValue: 'Listo' })}</Button>
            </div>
          </>
        )}

      </div>
    </Modal>
  );
}
